"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.readHierarchyOccurrenceScopes = readHierarchyOccurrenceScopes;
const OrcadDsnCfbStreamUtils_1 = require("./OrcadDsnCfbStreamUtils");
const OrcadDsnCfbTypes_1 = require("./OrcadDsnCfbTypes");
const OrcadDsnCfbHierarchyViewFrames_1 = require("./OrcadDsnCfbHierarchyViewFrames");
const OrcadDsnCfbPayloadIndex_1 = require("./OrcadDsnCfbPayloadIndex");
const MAX_HIERARCHY_DEPTH = 256;
function readHierarchyOccurrenceScopes(bytes, rootView, diagnostics, streamPath, branches = [], parents = new Map()) {
    const scopes = new Map();
    const namedPaths = new Map();
    const records = new Map();
    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachTerminatedPayload(bytes, (record) => records.set(record.offset, record));
    const payloadIndex = new OrcadDsnCfbPayloadIndex_1.OrcadDsnCfbPayloadIndex([...records.values()]);
    const propertyEnd = (offset) => {
        if (offset + 20 > bytes.length ||
            bytes[offset] !== 0x42 ||
            bytes[offset + 9] !== 0x42 ||
            OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 5) !== 0) {
            return undefined;
        }
        const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset + 10);
        const end = offset + 9 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 1);
        const terminator = offset + 12 + count * 8;
        if (end > bytes.length ||
            terminator + 8 > end ||
            !OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.hasBytes(bytes, terminator, [0xff, 0xe4, 0x5c, 0x39]) ||
            terminator + 8 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, terminator + 4) !== end) {
            return undefined;
        }
        return end;
    };
    const openBranch = (kindOffset, parent) => {
        const frame = (0, OrcadDsnCfbHierarchyViewFrames_1.readHierarchyViewBody)(bytes, kindOffset, payloadIndex, diagnostics, streamPath);
        if (!frame || frame.endOffset + 2 > bytes.length) {
            const nameEnd = kindOffset + 11 + (kindOffset + 11 <= bytes.length ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, kindOffset + 9) : 0);
            const nameRecord = payloadIndex.findUniqueContainingPayload(kindOffset);
            const name = (bytes[kindOffset] === 0x42 &&
                nameEnd > kindOffset + 11 &&
                nameRecord !== undefined &&
                nameEnd < nameRecord.offset + nameRecord.payloadByteLength &&
                bytes[nameEnd] === 0 &&
                !bytes.subarray(kindOffset + 11, nameEnd).some((byte) => byte < 0x20 || byte > 0x7e)) ?
                OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.bytesToAscii(bytes, kindOffset + 11, nameEnd)
                : undefined;
            branches.push({
                offset: kindOffset,
                viewName: name,
                state: OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyBranchDecodeState.Unframed,
            });
            return undefined;
        }
        const depth = parent === undefined ? 0 : parent.depth + 1;
        const withinBudget = depth < MAX_HIERARCHY_DEPTH;
        const offsets = withinBudget ? [...(parent?.offsets ?? []), kindOffset] : [];
        const reservedWord = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, kindOffset + 5);
        const declaredEnd = kindOffset + 9 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, kindOffset + 1);
        const name = frame.name;
        const rootMatches = kindOffset !== 0 || name === undefined || name.toUpperCase() === rootView.toUpperCase();
        if (declaredEnd !== frame.endOffset) {
            diagnostics.push({
                severity: 'warning',
                code: 'hierarchy-ownership-body-length-invalid',
                streamPath,
                offset: kindOffset + 1,
                message: `CFB hierarchy ownership: declared view boundary ${declaredEnd} disagrees with complete table boundary ${frame.endOffset}; branch ownership withheld`,
            });
        }
        if (reservedWord !== 0) {
            diagnostics.push({
                severity: 'warning',
                code: 'hierarchy-ownership-reserved-word-invalid',
                streamPath,
                offset: kindOffset + 5,
                message: `CFB hierarchy ownership: view ${JSON.stringify(name) ?? '<undecodable>'}: reserved word at header +5 is ${reservedWord}; branch ownership withheld`,
            });
        }
        if (name === undefined) {
            diagnostics.push({
                severity: 'warning',
                code: 'hierarchy-ownership-name-withheld',
                streamPath,
                offset: kindOffset + 9,
                message: 'CFB hierarchy ownership: branch name is not a complete printable counted string; its occurrence rows remain unbound',
            });
        }
        if (!withinBudget) {
            diagnostics.push({
                severity: 'warning',
                code: 'hierarchy-ownership-depth-withheld',
                streamPath,
                offset: kindOffset,
                message: `CFB hierarchy ownership: ownership resource budget of ${MAX_HIERARCHY_DEPTH} nested branches exceeded; this branch's ownership withheld`,
            });
        }
        if (!rootMatches) {
            diagnostics.push({
                severity: 'warning',
                code: 'hierarchy-ownership-root-name-mismatch',
                streamPath,
                offset: kindOffset,
                message: 'CFB hierarchy ownership: recorded root view disagrees with its storage identity; root ownership withheld',
            });
        }
        return {
            kindOffset,
            name,
            depth,
            offsets,
            cursor: frame.endOffset + 2,
            remaining: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, frame.endOffset),
            complete: true,
            valid: declaredEnd === frame.endOffset && reservedWord === 0 && withinBudget && rootMatches,
            leaves: [],
            children: [],
        };
    };
    const root = openBranch(0);
    if (!root) {
        diagnostics.push({
            severity: 'warning',
            code: 'hierarchy-ownership-root-unframed',
            streamPath,
            offset: 0,
            message: 'CFB hierarchy ownership: root body has no complete counted frame; ownership withheld',
        });
        return new Map();
    }
    const stack = [root];
    while (stack.length > 0) {
        const current = stack[stack.length - 1];
        if (current.complete && current.remaining > 0) {
            const childOffset = propertyEnd(current.cursor);
            if (childOffset === undefined || childOffset + 19 > bytes.length || bytes[childOffset + 8] !== 0x42) {
                current.complete = false;
                continue;
            }
            current.remaining--;
            const childKind = childOffset + 8;
            if (OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, childKind + 9) !== 0) {
                current.children.push(childKind);
                const child = openBranch(childKind, current);
                if (child === undefined) {
                    current.complete = false;
                }
                else {
                    stack.push(child);
                }
            }
            else {
                const end = childKind + 11 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, childKind + 1);
                if (end <= childOffset + 19 || end > bytes.length) {
                    current.complete = false;
                }
                else {
                    current.leaves.push(childOffset);
                    current.cursor = end;
                }
            }
            continue;
        }
        stack.pop();
        const resolved = current.complete && current.valid && current.name !== undefined;
        for (const offset of current.children) {
            parents.set(offset, {
                viewName: current.name,
                branchOffset: current.kindOffset,
                state: resolved ?
                    OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Resolved
                    : OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Withheld,
            });
        }
        branches.push({
            offset: current.kindOffset,
            viewName: current.name,
            state: !current.complete ? OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyBranchDecodeState.Incomplete
                : resolved ? OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyBranchDecodeState.Resolved
                    : OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyBranchDecodeState.Withheld,
        });
        if (!current.complete) {
            diagnostics.push({
                severity: 'warning',
                code: 'hierarchy-ownership-frame-withheld',
                streamPath,
                offset: current.kindOffset,
                message: `CFB hierarchy ownership: view ${JSON.stringify(current.name) ?? '<undecodable>'} has an incomplete child list; every direct ownership claim withheld`,
            });
        }
        for (const offset of current.leaves) {
            scopes.set(offset, {
                viewName: current.name,
                branchOffset: current.kindOffset,
                state: resolved ?
                    OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Resolved
                    : OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Withheld,
            });
        }
        if (current.name !== undefined) {
            if (resolved) {
                const path = JSON.stringify([streamPath, ...current.offsets]);
                const paths = namedPaths.get(current.name.toUpperCase()) ?? new Set();
                paths.add(path);
                namedPaths.set(current.name.toUpperCase(), paths);
                if (paths.size > 1) {
                    diagnostics.push({
                        severity: 'warning',
                        code: 'hierarchy-ownership-name-collision',
                        streamPath,
                        offset: current.kindOffset,
                        message: `CFB hierarchy ownership: view ${JSON.stringify(current.name)} occurs at distinct framed paths ${JSON.stringify([...paths])}; all applicable occurrence section evidence must agree`,
                    });
                }
            }
        }
        const parent = stack[stack.length - 1];
        if (parent !== undefined) {
            if (current.complete) {
                parent.cursor = current.cursor;
            }
            else {
                parent.complete = false;
            }
        }
    }
    return scopes;
}
//# sourceMappingURL=OrcadDsnCfbHierarchyScopes.js.map